<div class="container">

  <header class="page-heading">
    <h1>Lumpsum Investment Calculator</h1>
    <p>Calculate the maturity value and wealth gained on a one-time lumpsum mutual fund investment.</p>
  </header>

  <div class="card mb-lg">
    <div class="section-title">Lumpsum Investment Details</div>

    <div class="field-row-2">
      <div class="field-group" [class.has-error]="hasError('principal')">
        <label class="field-label" for="principal">Investment Amount</label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="principal" type="number"
            [(ngModel)]="principal"
            [min]="C.principal.min" [max]="C.principal.max" [step]="C.principal.step"
            (ngModelChange)="onFieldChange('principal')"
            (blur)="onFieldBlur('principal')"
            placeholder="e.g. 1,00,000"
            autocomplete="off"/>
        </div>
        <div class="field-hint">{{ C.principal.min | currency:'INR':'symbol':'1.0-0':'en-IN' }} – {{ C.principal.max | currency:'INR':'symbol':'1.0-0':'en-IN' }}</div>
        @if (hasError('principal')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('principal') }}
          </div>
        }
      </div>

      <div class="field-group" [class.has-error]="hasError('annualRate')">
        <label class="field-label" for="annualRate">Expected Annual Return</label>
        <div class="input-shell suffix">
          <input id="annualRate" type="number"
            [(ngModel)]="annualRate"
            [min]="C.rate.min" [max]="C.rate.max" [step]="C.rate.step"
            (ngModelChange)="onFieldChange('annualRate')"
            (blur)="onFieldBlur('annualRate')"
            placeholder="e.g. 12"
            autocomplete="off"/>
          <span class="affix right">%</span>
        </div>
        @if (hasError('annualRate')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('annualRate') }}
          </div>
        }
      </div>
    </div>

    <div class="field-group" [class.has-error]="hasError('tenureYears')">
      <label class="field-label" for="tenureYears">Investment Duration</label>
      <div class="input-shell suffix">
        <input id="tenureYears" type="number"
          [(ngModel)]="tenureYears"
          [min]="C.tenureYears.min" [max]="C.tenureYears.max" [step]="C.tenureYears.step"
          (ngModelChange)="onFieldChange('tenureYears')"
          (blur)="onFieldBlur('tenureYears')"
          placeholder="e.g. 10"
          autocomplete="off"/>
        <span class="affix right">Yr</span>
      </div>
      @if (hasError('tenureYears')) {
        <div class="field-error">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          {{ fieldError('tenureYears') }}
        </div>
      }
    </div>

    <div class="action-row">
      <button type="button" class="btn btn-primary" (click)="calculate()" [class.btn-disabled]="showFormErrors && !isFormValid">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
        Calculate
      </button>
      @if (calculated) {
        <button type="button" class="btn btn-secondary" (click)="reset()">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
          Reset
        </button>
      }
      @if (showFormErrors && !isFormValid) {
        <span class="form-error-hint">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          Fix the errors above to calculate
        </span>
      }
    </div>
  </div>

  @if (calculated && result) {
    <div class="summary-grid mb-md">
      <div class="summary-card c-blue">
        <div class="sc-icon"></div>
        <div class="sc-label">Maturity Value</div>
        <div class="sc-value">{{ fmt.currency(result.maturityValue) }}</div>
        <div class="sc-sub">after {{ tenureYears }} years</div>
      </div>
      <div class="summary-card c-green">
        <div class="sc-icon"></div>
        <div class="sc-label">Amount Invested</div>
        <div class="sc-value">{{ fmt.currency(result.totalInvested) }}</div>
        <div class="sc-sub">one-time</div>
      </div>
      <div class="summary-card c-red">
        <div class="sc-icon"></div>
        <div class="sc-label">Wealth Gained</div>
        <div class="sc-value">{{ fmt.currency(result.totalGain) }}</div>
        <div class="sc-sub">@ {{ annualRate }}% p.a.</div>
      </div>
    </div>

    <div class="card charts-card mb-md">
      <div class="section-title">Visual Breakdown</div>
      <div class="charts-top-row">
        <div class="chart-panel">
          <div class="chart-panel-label">Invested vs Gain</div>
          <div class="chart-canvas-wrap chart-canvas-pie">
            <canvas #breakdownRef></canvas>
          </div>
        </div>
        <div class="chart-panel chart-panel-bar">
          <div class="chart-panel-label">Corpus Growth</div>
          <div class="chart-canvas-wrap">
            <canvas #growthRef></canvas>
          </div>
        </div>
      </div>
    </div>

    <div class="card mb-md">
      <div class="section-title">Yearly Growth</div>
      <div class="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th class="t-left">Year</th>
              <th>Invested</th>
              <th class="t-balance">Corpus Value</th>
            </tr>
          </thead>
          <tbody>
            @for (row of result.years; track row.year) {
              <tr>
                <td class="t-left t-year">Year {{ row.year }}</td>
                <td>{{ fmt.currency(row.invested) }}</td>
                <td class="t-balance">{{ fmt.currency(row.balance) }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  }
</div>

<!-- ══ SEO CONTENT ══════════════════════════════════════════════════════ -->
<section class="container seo-content">
  <div class="card">
    <h2 class="section-title">How is Lumpsum Investment Return Calculated?</h2>
    <p>
      A lumpsum investment is a one-time amount invested in a mutual fund or other instrument,
      compounding at an assumed annual rate of return for the entire investment period.
    </p>
    <p class="emi-formula">FV = P &times; (1 + r)<sup>n</sup></p>
    <ul class="emi-formula-vars">
      <li><strong>P</strong> &mdash; Principal (one-time investment amount)</li>
      <li><strong>r</strong> &mdash; Expected annual rate of return</li>
      <li><strong>n</strong> &mdash; Investment duration in years</li>
    </ul>
    <p>
      For example, investing &#8377;1,00,000 as a lumpsum for 10 years at an expected 12% annual
      return grows to a corpus of about &#8377;3.1 lakh &mdash; roughly three times your original
      investment, purely from compounding.
    </p>

    <h2 class="section-title mt-sm">Frequently Asked Questions</h2>
    <div class="faq-item">
      <h3>Lumpsum or SIP &mdash; which is better?</h3>
      <p>
        A lumpsum works best when you have a large amount available and markets are reasonably
        valued, since your entire capital starts compounding immediately. An SIP suits regular
        income and volatile markets, since it averages your purchase cost over time.
      </p>
    </div>
    <div class="faq-item">
      <h3>Are lumpsum mutual fund returns guaranteed?</h3>
      <p>
        No. Returns depend on market performance and are not guaranteed. The rate you enter here is
        an assumption for planning purposes only.
      </p>
    </div>
    <p class="learn-crosslink">
      Read more: <a routerLink="/learn/sip-vs-lumpsum">SIP vs lump sum: what does the math actually say?</a>
    </p>
  </div>
</section>
